import type { ColumnsType } from 'antd/es/table';
import {
  Box,
  Colors,
  IcClose,
  IcCopy,
  IcDevice,
  IcEdit,
  IcMenuChange,
  IcScreen,
  IcTick,
  IcTrash,
  Pagination,
  Row,
  StatusTag,
  Table,
  Text,
} from 'basic-ui-lib';
import EditNameModal, {
  EditNameModalRef,
} from 'basic-ui-lib/components/Modal/EditNameModal';
import { ActionProps } from 'basic-ui-lib/components/Table/Table';
import { IScreen } from 'modules/dashboard/interfaces/api';
import dashboardApis from 'modules/dashboard/services/apis';
import {
  ScreenStatus,
  StatusScreenConfigs,
} from 'modules/dashboard/services/constants';
import useGetScreensInStore from 'modules/store/services/hooks/useListScreensInStore';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMessageError } from 'services/response';
import styled from 'styled-components';
import toast from 'utils/toast';
import ModalConfirm, { useModalConfirmRef } from 'widgets/ModalConfirm';
import { setPageLoading } from 'widgets/PageLoading';

interface ScreenTableProps {
  id: string;
}

const ScreenTable = ({ id }: ScreenTableProps) => {
  const modalConfirmRef = useModalConfirmRef();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const editNameModalRef = useRef<EditNameModalRef>(null);

  const { screensListInStore, screenListInStoreParam, mutate } = useGetScreensInStore(id || '');

  const columns: ColumnsType<IScreen> = [
    {
      title: 'SCREEN NAME',
      dataIndex: 'playerName',
      render: (text) => (
        <Row gap={12} alignItems='center'>
          <Box
            display='flex'
            justifyContent='center'
            alignItems='center'
            width={40}
            height={40}
            backgroundColor={Colors.neutral.bg[2]}
          >
            <IcScreen width={24} height={24} fill={Colors.neutral.bg[7]} />
          </Box>
          <Text content_2 color='primary.6'>
            {text}
          </Text>
        </Row>
      ),
    },
    {
      title: 'PLAYLIST NAME',
      dataIndex: 'playListName',
    },
    {
      title: 'PAIRING',
      dataIndex: 'code',
      render: (text) => (
        <Box
          width='80px'
          display='flex'
          justifyContent='center'
          alignItems='center'
          gap={4}
          backgroundColor={Colors.orange[2]}
          borderRadius={100}
          padding='2px 0'
        >
          <Text body_1 medium color='neutral.text.1'>
            {text}
          </Text>
        </Box>
      ),
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      render: (status) => {
        const config = StatusScreenConfigs[status as ScreenStatus];
        return (
          <StatusTag
            label={config?.label ?? status}
            backgroundColor={config?.backgroundColor}
            color={config?.color}
          />
        );
      },
    },
    {
      title: 'DEVICE TYPE',
      dataIndex: 'deviceType',
    },
    {
      title: 'OPERATION',
      dataIndex: 'operatingSystem',
    },
    {
      title: 'LAST ACTIVE',
      dataIndex: 'lastTimeActive',
    },
  ];

  const actions: ActionProps<IScreen>[] = [
    {
      label: 'Refresh',
      icon: <IcMenuChange width={16} height={16} />,
      onClick: (item) => handleRefreshScreen(item.id),
    },
    {
      label: 'Copy Link',
      icon: <IcCopy width={16} height={16} />,
      onClick: (item) => {
        navigator.clipboard.writeText(item.id);
        toast.success('Copied to clipboard');
      },
    },
    {
      label: 'Edit Name',
      icon: <IcEdit width={16} height={16} />,
      onClick: (item) => {
        editNameModalRef.current?.open({
          currentName: item.playerName,
          id: item.id,
          onFinish: handleUpdateName,
          modalTitle: 'Edit Screen Name',
        });
      },
    },
    {
      label: 'Edit Content',
      icon: <IcDevice width={16} height={16} />,
      onClick: (item) => navigate(`/screen/${item.id}`),
    },
    {
      label: (item) =>
        item.status === ScreenStatus.ONLINE ? 'Deactivate' : 'Reactivate',
      icon: (item) =>
        item.status === ScreenStatus.ONLINE ? (
          <IcClose width={16} height={16} />
        ) : (
          <IcTick width={16} height={16} />
        ),
      onClick: (item) => handleChangeStatus(item),
      hidden: (item) => item.status === ScreenStatus.DRAFT,
    },
    {
      label: 'Delete Screen',
      icon: <IcTrash width={16} height={16} />,
      onClick: (item) => onDeleteScreen(item.id),
    },
  ];

  const handleRefreshScreen = async (screenId: string) => {
    try {
      setPageLoading(true);
      const res = await dashboardApis.refreshScreen(screenId);
      if (res?.data?.data) {
        toast.success('Screen refreshed successfully');
        mutate({ page: currentPage, size: 10 });
      }
    } catch (error) {
      toast.error(getMessageError(error));
    } finally {
      setPageLoading(false);
    }
  };

  const handleDeleteScreen = async (screenId: string) => {
    try {
      setPageLoading(true);
      const res = await dashboardApis.deleteScreen(screenId);
      if (res?.data?.data) {
        toast.success('Screen deleted successfully');
        mutate({ page: currentPage, size: 10 });
      }
    } catch (error) {
      toast.error(getMessageError(error));
    } finally {
      setPageLoading(false);
    }
  };

  const onDeleteScreen = (screenId: string) => {
    modalConfirmRef.current?.open(() => handleDeleteScreen(screenId), {
      message:
        'Are you sure you want to delete this screen? This action cannot be undone.',
    });
  };

  const handleChangeStatus = async (item: IScreen) => {
    try {
      const newStatus =
        item.status === ScreenStatus.ONLINE
          ? ScreenStatus.DEACTIVATED
          : ScreenStatus.ONLINE;
      const res = await dashboardApis.updateStatusScreen({
        id: item.id,
        status: newStatus,
      });
      if (res?.data?.data) {
        toast.success(
          item.status === ScreenStatus.ONLINE
            ? 'Screen deactivated successfully'
            : 'Screen reactivated successfully',
        );
        mutate({ page: currentPage, size: 10 });
      }
    } catch (error) {
      toast.error(getMessageError(error) || 'Failed to update screen status');
    }
  };

  const handleUpdateName = async (id: string, newName: string) => {
    try {
      await dashboardApis.updateNameScreen({ screenId: id, name: newName });
      toast.success('Name updated successfully');
      mutate({
        ...screenListInStoreParam,
      });
    } catch (error) {
      toast.error(
        getMessageError(error) || 'An error occurred while updating the name',
      );
    }
  };
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    mutate({ ...screenListInStoreParam, page, size: 10 });
  };

  return (
    <TableBox>
      <Table<IScreen>
        className='custom-table'
        columns={columns}
        dataSource={screensListInStore?.content ?? []}
        scroll={{ x: 'max-content' }}
        pagination={false}
        actions={actions}
      />
      <Pagination
        totalItems={screensListInStore?.totalElements ?? 0}
        itemsPerPage={10}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        labelDisplay='screens'
        showRowPerPage={true}
        maxVisiblePages={3}
      />
      <ModalConfirm ref={modalConfirmRef} />
      <EditNameModal ref={editNameModalRef} />
    </TableBox>
  );
};

export default ScreenTable;

const TableBox = styled.div`
  width: 100%;
`;
